import { useCallback, useEffect, useState } from 'react';

const NEWS_URL = 'https://newsdata.io/api/1/news?apikey=pub_101023249e5465451713483a2c9975ee0a196&language=en&country=us';

const formatDate = (dateString) => {
    if (!dateString) {
        return '';
    }

    const date = new Date(dateString);
    return Number.isNaN(date.getTime())
        ? ''
        : new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        }).format(date);
};

const ShowNews = () => {
    const [articles, setArticles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchNews = useCallback(async (signal) => {
        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(NEWS_URL, { signal });
            if (!response.ok) {
                throw new Error(`News service returned an error (${response.status}).`);
            }

            const data = await response.json();
            if (data.status !== 'success') {
                throw new Error(data.results?.message || 'The news service could not load articles right now.');
            }

            setArticles(Array.isArray(data.results) ? data.results : []);
        } catch (fetchError) {
            if (fetchError.name !== 'AbortError') {
                setError(fetchError.message || 'Unable to load the news feed. Please try again.');
                setArticles([]);
            }
        } finally {
            if (!signal.aborted) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchNews(controller.signal);
        return () => controller.abort();
    }, [fetchNews, refreshKey]);

    return (
        <div className="news-feed">
            <div className="news-feed-toolbar">
                <p className="news-feed-updated">
                    {isLoading ? 'Requesting articles…' : `${articles.length} articles returned`}
                </p>
                <button
                    className="btn btn-outline-secondary news-refresh-button"
                    type="button"
                    onClick={() => setRefreshKey((key) => key + 1)}
                    disabled={isLoading}
                >
                    <i className="fas fa-arrows-rotate" aria-hidden="true"></i>
                    <span>{isLoading ? 'Loading…' : 'Fetch again'}</span>
                </button>
            </div>

            {isLoading && (
                <div className="news-loading" role="status" aria-live="polite">
                    <span className="news-loading-spinner" aria-hidden="true"></span>
                    <span>Waiting for the API response…</span>
                </div>
            )}

            {!isLoading && error && (
                <div className="news-message news-message-error" role="alert">
                    <i className="fas fa-circle-exclamation" aria-hidden="true"></i>
                    <div>
                        <strong>The news request failed.</strong>
                        <p>{error}</p>
                        <p>The free news service may be temporarily unavailable or out of daily requests.</p>
                    </div>
                </div>
            )}

            {!isLoading && !error && articles.length === 0 && (
                <div className="news-message">
                    The API returned no articles for this request.
                </div>
            )}

            {!isLoading && !error && articles.length > 0 && (
                <div className="news-card-grid">
                    {articles.map((article, index) => {
                        const title = article.title || 'Untitled story';
                        const date = formatDate(article.pubDate);
                        const source = article.source_name || article.source_id || 'News source';
                        const storyUrl = typeof article.link === 'string' && /^https?:\/\//i.test(article.link)
                            ? article.link
                            : null;

                        return (
                            <article
                                className="news-card"
                                key={article.article_id || article.link || `${title}-${index}`}
                            >
                                {article.image_url ? (
                                    storyUrl ? (
                                        <a
                                            className="news-card-image-link"
                                            href={storyUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            tabIndex="-1"
                                            aria-hidden="true"
                                        >
                                            <img className="news-card-image" src={article.image_url} alt="" loading="lazy" />
                                        </a>
                                    ) : (
                                        <img className="news-card-image" src={article.image_url} alt="" loading="lazy" />
                                    )
                                ) : (
                                    <div className="news-card-image-placeholder" aria-hidden="true">
                                        <i className="far fa-newspaper"></i>
                                    </div>
                                )}
                                <div className="news-card-content">
                                    <div className="news-card-meta">
                                        <span className="news-source">{source}</span>
                                        {date && <time dateTime={article.pubDate}>{date}</time>}
                                    </div>
                                    <h3>
                                        {storyUrl ? (
                                            <a href={storyUrl} target="_blank" rel="noreferrer">{title}</a>
                                        ) : title}
                                    </h3>
                                    {article.description && (
                                        <p>{article.description}</p>
                                    )}
                                    {storyUrl && (
                                        <a className="news-read-link" href={storyUrl} target="_blank" rel="noreferrer">
                                            Read story <span aria-hidden="true">↗</span>
                                        </a>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ShowNews;
